import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import throttle from 'lodash.throttle';
import md5 from 'md5';
import apiRequest from '../utils/apiRequest';
import findIdByPath from '../utils/findIdByPath';

const StyledNote = styled.div``;

const Text = styled.a`
  display: block;
  cursor: pointer;
  text-decoration: none;
  color: #161e21;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.4285714286em;
`;

const Edit = styled.textarea`
  resize: vertical;
  width: 100%;
  margin: 0;
  border: none;
  background: transparent;
  color: #161e21;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.4285714286em;
  outline: 0;
  height: 10rem;
  padding: 0;
  font-family: 'Source Sans Pro', sans-serif;
`;

const DoneButton = styled.a`
  cursor: pointer;
  display: inline-block;
  color: ${props => props.theme.tertiary};
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.3s ease-in-out;
  margin: 0 1em 0 0;

  ${props =>
    !props.disabled &&
    css`
      &:hover {
        text-decoration: underline;
      }
    `};

  ${props =>
    props.disabled &&
    css`
      color: #161e21;
      opacity: 0.3;
      cursor: wait;
    `};
`;

class Note extends Component {
  state = {
    editing: false,
    saving: false,
    deleting: false,
    creatingNew: false,
    id: this.props.data ? this.props.data._id : null,
    text: this.props.data ? this.props.data.text : ''
  };

  currentStateOfTheField = '';

  componentWillReceiveProps(nextProps) {
    const { text, id, editing } = this.state;

    if (!editing && nextProps.data) {
      if (text !== nextProps.data.text || id !== nextProps.data._id) {
        this.setState({ text: nextProps.data.text, id: nextProps.data._id });
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { editing, creatingNew } = this.state;

    if (editing && !creatingNew && prevState.creatingNew) {
      this.textArea.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.endEditingOnEscape);
  }

  startEditing = () => {
    const { data } = this.props;

    this.setState({
      editing: true
    });

    if (!data) this.createNew();
    document.addEventListener('keyup', this.endEditingOnEscape);
  };

  createNew = async () => {
    const { chapterKey } = this.props;

    this.setState({ creatingNew: true });

    const response = await apiRequest('scribble', 'scribbles', 'post', {
      type: findIdByPath(),
      section: chapterKey,
      text: ''
    });

    this.setState({
      creatingNew: false,
      id: response._id
    });
  };

  typeNote = e => {
    const inputValue = e.target.value;
    this.currentStateOfTheField = inputValue;
    this.setState({ text: inputValue }, this.save);
  };

  save = throttle(async () => {
    const { fetchNotes } = this.props;
    const { id, text } = this.state;

    this.setState({ saving: true });

    await apiRequest('scribble', `scribbles/${id}`, 'put', {
      text
    });

    if (md5(this.currentStateOfTheField) === md5(text)) {
      this.setState({ saving: false });
      fetchNotes();
    }
  }, 1000);

  delete = async () => {
    const { fetchNotes } = this.props;
    const { id } = this.state;

    this.setState({ deleting: true });

    await apiRequest('scribble', `scribbles/${id}`, 'delete');

    fetchNotes(() => {
      this.setState({
        deleting: false,
        editing: false,
        id: null,
        text: ''
      });
    });
  };

  endEditingOnEscape = e => {
    const { deleting, saving, creatingNew } = this.props;

    if (e.keyCode === 27) {
      if (!deleting && !saving && !creatingNew) {
        this.setState({
          editing: false
        });
      }
    }
  };

  render() {
    const { data } = this.props;
    const { editing, creatingNew, text, saving, deleting } = this.state;

    return (
      <StyledNote>
        {editing ? (
          <div>
            <Edit
              autoFocus
              placeholder={creatingNew ? 'Lager notat…' : 'Begynn å skrive…'}
              disabled={creatingNew || deleting}
              innerRef={textArea => (this.textArea = textArea)}
              onChange={this.typeNote}
              value={text}
            />
            <DoneButton
              href="#"
              disabled={saving || deleting}
              onClick={e => {
                e.preventDefault();
                if (!saving && !deleting) {
                  document.removeEventListener(
                    'keyup',
                    this.endEditingOnEscape
                  );
                  this.setState({ editing: false });
                }
              }}
            >
              {saving ? 'Lagrer…' : 'Ferdig'}
            </DoneButton>
            {data !== null && (
              <DoneButton
                href="#"
                disabled={saving || deleting}
                onClick={e => {
                  e.preventDefault();
                  if (!saving && !deleting) this.delete();
                }}
              >
                {deleting ? 'Sletter…' : 'Slett'}
              </DoneButton>
            )}
          </div>
        ) : (
          <Text
            href="#"
            onClick={e => {
              e.preventDefault();
              this.startEditing();
            }}
          >
            {data && data.html ? (
              <div dangerouslySetInnerHTML={{ __html: data.html }} />
            ) : (
              'Ingen notater for dette kapitlet enda.'
            )}
          </Text>
        )}
      </StyledNote>
    );
  }
}

const noteType = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  html: PropTypes.string.isRequired
});

Note.propTypes = {
  data: noteType,
  chapterKey: PropTypes.number.isRequired,
  fetchNotes: PropTypes.func.isRequired
};

export default Note;
export { noteType };



// WEBPACK FOOTER //
// ./src/components/Note.js